import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";
import { X } from "lucide-react";

export default function News() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/articles").then((r) => setItems(r.data)).catch(() => {}); }, []);

  return (
    <section id="news" data-testid="news-section" className="bg-icdh-white py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <Reveal><Overline>{t.news.overline}</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-tight text-icdh-navy">{t.news.title}</h2></Reveal>
          </div>
        </div>

        {items.length === 0 ? (
          <p className="mt-16 text-icdh-navy/50">{t.news.empty}</p>
        ) : (
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {items.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.1}>
                <article data-testid={`news-card-${i}`} onClick={() => setActive(a)}
                  className="group cursor-pointer border border-icdh-navy/10 hover:border-icdh-red transition-colors duration-500 h-full flex flex-col">
                  <div className="overflow-hidden aspect-[4/3]">
                    <img src={a.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <span className="text-xs uppercase tracking-[0.2em] text-icdh-red">
                      {a.category === "event" ? t.news.event : t.news.overline.split(" ")[0]}{a.event_date ? ` · ${a.event_date}` : ""}
                    </span>
                    <h3 className="mt-4 font-serif text-2xl tracking-tight text-icdh-navy">{a.title[lang]}</h3>
                    <p className="mt-3 text-icdh-navy/60 text-sm flex-1">{a.excerpt[lang]}</p>
                    <span className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-icdh-navy group-hover:text-icdh-red transition-colors">{t.news.read} →</span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {active && (
        <div data-testid="news-modal" className="fixed inset-0 z-[60] bg-icdh-navy/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-icdh-white max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img src={active.image} alt="" className="w-full aspect-video object-cover" />
              <button data-testid="news-modal-close" onClick={() => setActive(null)} className="absolute top-4 right-4 h-10 w-10 bg-icdh-white flex items-center justify-center"><X size={18} /></button>
            </div>
            <div className="p-8 md:p-12">
              <span className="text-xs uppercase tracking-[0.2em] text-icdh-red">{active.category === "event" ? t.news.event : ""}{active.event_date ? ` · ${active.event_date}` : ""}</span>
              <h3 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight text-icdh-navy">{active.title[lang]}</h3>
              <p className="mt-6 text-icdh-navy/75 leading-relaxed">{active.body[lang]}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
